import { useState, useEffect } from "react";
import { getSDKUrl } from "./constants";

// Custom hook for loading the Preorder SDK
export const usePreorderSDK = () => {
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const loadSDK = async () => {
      try {
        setIsLoading(true);
        setSdkError(null);

        // Check if SDK is already loaded
        if (window.preorder) {
          setSdkLoaded(true);
          setIsLoading(false);
          console.log("Preorder SDK already loaded");
          return;
        }

        // Create script element
        const script = document.createElement("script");
        // Автоматический выбор URL в зависимости от окружения
        script.src = getSDKUrl();
        script.async = true;
        script.type = "text/javascript";

        // Set timeout for script loading
        const timeoutId = setTimeout(() => {
          if (!window.preorder) {
            setSdkError("SDK loading timeout - script took too long to load");
            setIsLoading(false);
          }
        }, 10000); // 10 second timeout

        // Handle script load success
        script.onload = () => {
          clearTimeout(timeoutId);
          if (window.preorder) {
            setSdkLoaded(true);
            setSdkError(null);
            console.log("Preorder SDK loaded successfully", {
              version: window.preorder.version,
              isOpen: window.preorder.isOpen,
            });
          } else {
            setSdkError(
              "SDK object not found after script load - check console for errors"
            );
          }
          setIsLoading(false);
        };

        // Handle script load error
        script.onerror = (event) => {
          clearTimeout(timeoutId);
          const errorMessage = `Failed to load Preorder SDK script from ${script.src}`;
          console.error("SDK script load error:", event);
          setSdkError(errorMessage);
          setIsLoading(false);
        };

        // Add script to document
        document.head.appendChild(script);

        // Cleanup function
        return () => {
          clearTimeout(timeoutId);
          if (script.parentNode) {
            script.parentNode.removeChild(script);
          }
        };
      } catch (error) {
        const errorMessage = `Error loading SDK: ${
          error instanceof Error ? error.message : "Unknown error"
        }`;
        console.error("SDK loading error:", error);
        setSdkError(errorMessage);
        setIsLoading(false);
      }
    };

    loadSDK();
  }, [retryCount]); // Re-run when retryCount changes

  const retry = () => {
    setRetryCount((prev) => prev + 1);
    setSdkLoaded(false);
    setSdkError(null);
  };

  return {
    sdkLoaded,
    sdkError,
    isLoading,
    sdk: window.preorder,
    retry,
    retryCount,
  };
};
